import React, { FC, useEffect, useState } from "react";
import { FormProvider, useForm, useFormState, useWatch } from "react-hook-form";
import GradingQuestions from "../GradingQuestions/GradingQuestions";
import { Button } from "src/components/ui/button";
import { AlertModal } from "src/modals";
import { AlertTriangle } from "lucide-react";
import useSubmitGrading from "src/hooks/useObservationForm/useSubmitGrading/useSubmitGrading";

interface Props {
	formInstanceId: number;
	gradePerformance: IGreatConversationGradePerformance;
	gradingComments: string;
	graded: boolean;
	handlecloseReportAnswerModalClick?: () => void;
}

const GradingForm: FC<Props> = ({
	formInstanceId,
	gradePerformance,
	gradingComments,
	graded,
	handlecloseReportAnswerModalClick
}) => {
	const methods = useForm<GradingFormState>({
		mode: "onChange",
		reValidateMode: "onChange",
		defaultValues: {
			gradePerformance: undefined,
			gradingComments: ""
		}
	});

	const { control, handleSubmit, setValue: setGradingFormValue } = methods;

	const gradePerformanceValue = useWatch({
		control,
		name: "gradePerformance"
	});

	const gradingCommentsValue = useWatch({
		control,
		name: "gradingComments"
	});

	const {
		mutate: submitGrading,
		isLoading,
		isSuccess,
		isError
	} = useSubmitGrading({ formInstanceId });

	const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);

	const onValid = () => {
		submitGrading({
			id: formInstanceId,
			graded: true,
			great_conversation_grade_performance:
				gradePerformanceValue as IGreatConversationGradePerformance,
			great_conversation_grading_comments: gradingCommentsValue
		});
	};

	const closeModals = () => {
		setIsConfirmationOpen(false);

		if (handlecloseReportAnswerModalClick) handlecloseReportAnswerModalClick();
	};

	const onInvalid = () => {
		setIsConfirmationOpen(false);
	};

	const submitGradingClick = handleSubmit(onValid, onInvalid);

	const { errors } = useFormState({ control });

	useEffect(() => {
		if (graded) {
			setGradingFormValue("gradePerformance", gradePerformance);
			setGradingFormValue("gradingComments", gradingComments);
		}
	}, []);

	useEffect(() => {
		if (!isSuccess && !isError) return;

		closeModals();
	}, [isSuccess, isError]);

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onValid, onInvalid)}>
					<div
						className="flex flex-col gap-4"
						data-testid="component.GradingForm"
					>
						<GradingQuestions />

						<Button
							type="button"
							className="self-end mt-8"
							onClick={() => setIsConfirmationOpen(true)}
							disabled={
								graded ||
								Boolean(errors.gradePerformance) ||
								Boolean(errors.gradingComments)
							}
						>
							{graded ? "Grading Submitted" : "Submit"}
						</Button>
					</div>
				</form>
			</FormProvider>
			<AlertModal
				isOpen={isConfirmationOpen}
				onClose={() => setIsConfirmationOpen(false)}
				ModalControllers={
					<>
						<Button
							variant="outline"
							size="lg"
							onClick={() => setIsConfirmationOpen(false)}
						>
							Cancel
						</Button>
						<Button onClick={submitGradingClick} disabled={isLoading} size="lg">
							{isLoading ? "Loading..." : "Submit"}
						</Button>
					</>
				}
			>
				<AlertTriangle size={48} data-testid="warning-icon" />
				<p>Are you sure you want to submit Grading?</p>
			</AlertModal>
		</>
	);
};

export default GradingForm;
